import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { getIsTrainIncident } from '@rsos/base-utils';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { ACTIVE_ASSAILANT } from '@rsos/constants/alertTypes';
import { ALERTS, EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { ESRI } from '@rsos/constants/mapProviders';
import { MARKER_TYPE_GRAPHIC } from '@rsos/constants/markerTypes';
import { createCircle } from '@rsos/maps/interface/circles';
import { createMarker } from '@rsos/maps/interface/markers';
import { createGraphicsLayer } from '@rsos/maps/providers/esri/graphics/layers';
import {
  filteredAAAlertsSelector,
  filteredRegularAlertsSelector,
  filteredTrainAlertsSelector,
  filteredTestAlertsSelector,
} from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import isLocationRestricted from '@rsos/utils/isLocationRestricted';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import usePrevious from '@rsos/utils/usePrevious';
import {
  ALERT_MARKER_CIRCLES_LAYER,
  ALERT_MARKERS_LAYER,
  AA_ALERT_MARKER_CIRCLES_LAYER,
  AA_ALERT_MARKERS_LAYER,
  TRAIN_ALERT_MARKER_CIRCLES_LAYER,
  TRAIN_ALERT_MARKERS_LAYER,
  TEST_ALERT_MARKER_CIRCLES_LAYER,
  TEST_ALERT_MARKERS_LAYER,
} from '../../../../../constants/esriMapLayerIds';
import { isIgnoreType } from '../calls/ignoreUtils';
import { AAAMarkers } from './AAAMarkers';
import { AlertMarkers } from './AlertMarkers';
import { TestMarkers } from './TestMarkers';
import { TrainMarkers } from './TrainMarkers';
import {
  getAlertMarkerIcon,
  getAlertMarkerSize,
  getAlertCircleColor,
} from './alertMarkerIcons';
import { updateMarkerIcon, resolveMarkers, isRegularAlert } from './utils';

const baseEsriCircle = {
  type: 'simple-fill',
  style: 'solid',
  color: getAlertCircleColor(),
  outline: { width: 2, color: getAlertCircleColor() },
};

let prevPopupData = null;
const portalMount = document.createElement('div');

/**
 * @param {String} activeQueueTab - ALERTS or EMERGENCY_CALLS
 * @param {Object} hoveredAlert - ID of the hovered alert ID
 * @param {String} hoveredFrom - Where user hovers, either 'queue' or 'map'
 * @param {Function} onMarkerAdd - Callback function when a new marker is drawn
 * @param {Function} onMarkerClick - onClick handler for markers
 * @param {Function} onMarkerHover - Hover effect handler for markers
 * @param {Function} onRemoveID - function to set state in JV for removed alert
 * @param {String} removedID - ID of removed Alert or Call
 */

const AllAlertMarkers = ({
  activeQueueTab,
  hoveredAlert,
  map,
  hoveredFrom,
  onMarkerAdd,
  onMarkerClick,
  onMarkerHover,
  onRemoveID,
  removedID,
  isMapIconDisabled,
}) => {
  // local state variables
  const [alertMarkers, setAlertMarkers] = useState([]);
  const [alertMarkersLayer, setAlertMarkersLayer] = useState(null);
  const [alertMarkerCircles, setAlertMarkerCircles] = useState([]);
  const [alertMarkerCirclesLayer, setAlertMarkerCirclesLayer] = useState(null);

  const [aaAlertMarkers, setAAAlertMarkers] = useState([]);
  const [aaAlertMarkersLayer, setaaAlertMarkersLayer] = useState(null);
  const [aaAlertMarkerCircles, setaaAlertMarkerCircles] = useState([]);
  const [aaAlertMarkerCirclesLayer, setaaAlertMarkerCirclesLayer] =
    useState(null);

  const [trainMarkers, setTrainMarkers] = useState([]);
  const [trainMarkersLayer, setTrainMarkersLayer] = useState(null);
  const [trainMarkerCircles, setTrainMarkerCircles] = useState([]);
  const [trainMarkerCirclesLayer, setTrainMarkerCirclesLayer] = useState(null);

  const [testMarkers, setTestMarkers] = useState([]);
  const [testMarkersLayer, setTestMarkersLayer] = useState(null);
  const [testMarkerCircles, setTestMarkerCircles] = useState([]);
  const [testMarkerCirclesLayer, setTestMarkerCirclesLayer] = useState(null);

  const [alertMarkersClickHandler, setAlertMarkersClickHandler] =
    useState(null);
  const [alertMarkersHoverHandler, setAlertMarkersHoverHandler] =
    useState(null);
  const [alertMarkerHoveredID, setAlertMarkerHoveredID] = useState(null);

  const [popupData, setPopupData] = useState(null);

  // selectors
  const aaAlerts = useSelector(state =>
    filteredAAAlertsSelector(state.incidents),
  );
  const alerts = useSelector(state =>
    filteredRegularAlertsSelector(state.incidents),
  );
  const trainAlerts = useSelector(state =>
    filteredTrainAlertsSelector(state.incidents),
  );
  const testAlerts = useSelector(state =>
    filteredTestAlertsSelector(state.incidents),
  );
  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);
  // The markers follow the single-slot IDWithNewAlert, which a backfill that
  // adds or changes several alerts cannot express, so it redraws them all.
  const alertsBackfilledAt = useSelector(
    state => state.incidents.alertsBackfilledAt,
  );
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const selectedCall = useMemo(
    () => callQueue[selectedCallerID],
    [callQueue, selectedCallerID],
  );

  const hasLocation = getMostRecentLoc(callQueue[selectedCallerID]);

  const isAllView = useMemo(
    () =>
      (!selectedAlertID && (!selectedCallerID || !hasLocation)) ||
      isIgnoreType(selectedCall),
    [selectedAlertID, selectedCallerID, hasLocation, selectedCall],
  );

  // reused connectors to handlers coming from props
  const handleAlertMarkerAdd = () => {
    onMarkerAdd(ALERTS);
  };

  const handleAlertMarkerHover = alertID => {
    onMarkerHover(alertID, 'map', ALERTS);
  };

  const handleAlertMarkerClick = alertID => {
    onMarkerClick(alertID, ALERTS, isAllView);
  };

  const handleAlertMarkerRemove = alertID => {
    onRemoveID(alertID);
  };

  // Esri hover handler for all layers combined
  const esriMarkerHoverHandler = () => {
    if (isMapIconDisabled) return;
    // Guard against missing view - can happen during initialization or cleanup
    if (!map?.mapInstance?.view) return;
    /*
     As per ESRI way of handling hover - watching every mouse move to hit the target, we keep firing hover Redux action
     on every mouse move. While within handler, we don't have an access to how variables/Redux evolves in outter scope.
     this local flag is to ensure the hover action is fired once when off target
    */
    let isAlreadyHovered = false;

    const allAlertCircleMarkers = [
      ...alertMarkerCircles,
      ...aaAlertMarkerCircles,
      ...trainMarkerCircles,
      ...testMarkerCircles,
    ];
    const allAlertMarkers = [
      ...alertMarkers,
      ...aaAlertMarkers,
      ...trainMarkers,
      ...testMarkers,
    ];
    const allAlerts = { ...alerts, ...aaAlerts, ...trainAlerts, ...testAlerts };
    return map.mapInstance.view.on('pointer-move', evt => {
      const options = {
        include: [
          alertMarkersLayer,
          aaAlertMarkersLayer,
          trainMarkersLayer,
          testMarkersLayer,
        ],
      };
      if (isAllView) {
        return map.mapInstance.view.hitTest(evt, options).then(response => {
          if (response.results.length) {
            allAlertMarkers.find(marker => {
              // If marker is hovered over, update it
              if (
                marker?.instance?.uid === response?.results[0]?.graphic?.uid
              ) {
                // Marker will change based on the hoveredAlert hook
                setAlertMarkerHoveredID(marker.custom.alertID);
                isAlreadyHovered = false;
              }
            });
          } else {
            if (!isAlreadyHovered) {
              setAlertMarkerHoveredID(null);
              isAlreadyHovered = true;
            }
          }
        });
      } else {
        // this handing can't be placed into [hoveredAlert] useEffect. It doesn't distunguish between historic location markers
        return map.mapInstance.view.hitTest(evt, options).then(response => {
          if (response?.results?.length) {
            allAlertMarkers.forEach((marker, index) => {
              const isHoveredMarker =
                marker?.instance?.uid === response?.results[0]?.graphic?.uid;
              updateMarkerIcon(
                marker,
                allAlerts[marker?.custom?.alertID],
                activeQueueTab,
                isAllView,
                index === 0,
                isHoveredMarker ? 'hover' : undefined,
              );

              if (isHoveredMarker) {
                setAlertMarkerHoveredID(marker.custom.alertID);
                const data = allAlerts[marker.custom.alertID];
                handlePopup({ alert: data, index });

                const circle = allAlertCircleMarkers[index];
                const circleColor =
                  marker?.instance?.uid === response?.results[0]?.graphic?.uid
                    ? getAlertCircleColor()
                    : getAlertCircleColor(index > 0);

                const symbolOptions = {
                  ...baseEsriCircle,
                  color: circleColor,
                  outline: {
                    ...baseEsriCircle.outline,
                    color: circleColor,
                  },
                };

                /*
                  The optional chaining in circle?.changeCircleColor prevents an error.
                  TODO:
                  There is currently a race condition in drawMarkers where marker and circle state is set asynchronously.
                  This results in multiple esriMarkerHoverHandlers created -- one of which will have incorrect state in
                  its scope and will sometimes cause a nasty error. We need to conslidate marker and circle promises to
                  resolve in a single batch so that we can set marker and circle state atomically.
                */
                circle?.changeCircleColor({ symbolOptions });
              }
            });
          } else {
            setAlertMarkerHoveredID(null);
            handlePopup();
            allAlertMarkers.forEach((marker, index) => {
              updateMarkerIcon(
                marker,
                allAlerts[marker?.custom?.alertID],
                activeQueueTab,
                isAllView,
                index === 0,
              );

              const circle = allAlertCircleMarkers[index];
              if (circle) {
                const circleColor = getAlertCircleColor(index > 0);
                circle.changeCircleColor({
                  symbolOptions: {
                    ...baseEsriCircle,
                    color: circleColor,
                    outline: {
                      ...baseEsriCircle.outline,
                      color: circleColor,
                    },
                  },
                });
              }
            });
          }
        });
      }
    });
  };

  // Esri marker click handler for all layers combined
  const esriMarkerClickHandler = () => {
    if (isMapIconDisabled) return;
    // Guard against missing view - can happen during initialization or cleanup
    if (!map?.mapInstance?.view) return;
    const opts = {
      include: [
        alertMarkersLayer,
        aaAlertMarkersLayer,
        trainMarkersLayer,
        testMarkersLayer,
      ],
    };
    const allAlertMarkers = [
      ...alertMarkers,
      ...aaAlertMarkers,
      ...trainMarkers,
      ...testMarkers,
    ];
    return map.mapInstance.view.on('click', evt => {
      return map.mapInstance.view.hitTest(evt, opts).then(response => {
        if (response.results.length) {
          const clickedMarker = allAlertMarkers.find(marker => {
            return marker?.instance?.uid === response?.results[0]?.graphic?.uid;
          });
          if (clickedMarker) {
            handleAlertMarkerClick(clickedMarker.custom.alertID);
            map.mapInstance.view.popup.visible = false;
          }
        }
      });
    });
  };

  // function to add/remove marker hover handler
  const handleMarkerHover = () => {
    switch (map.mapProvider) {
      case ESRI: {
        if (alertMarkersHoverHandler) {
          alertMarkersHoverHandler.remove();
        }
        const hoverHandler = esriMarkerHoverHandler();
        setAlertMarkersHoverHandler(hoverHandler);
        break;
      }
      default:
        return null;
    }
  };

  // function to add/remove marker click handler
  const handleMarkerClick = () => {
    switch (map.mapProvider) {
      case ESRI:
        if (alertMarkersClickHandler) {
          alertMarkersClickHandler.remove();
          setAlertMarkersClickHandler(null);
        }
        if (isAllView) {
          const clickHandler = esriMarkerClickHandler();
          setAlertMarkersClickHandler(clickHandler);
        }
        break;
      default:
        return null;
    }
  };

  /**
   * Function to handle showing/hiding a popup for a given alert marker
   * @param {Object} data - the popup's data used for rendering AlertPopup
   */
  function handlePopup(data) {
    if (
      (!data && prevPopupData) ||
      data?.alert.location_history?.length === 0
    ) {
      prevPopupData = null;
      setPopupData(null);
      map.mapInstance.view.popup.location = null;
      map.mapInstance.view.popup.content = null;
      map.mapInstance.view.popup.visible = false;
    } else if (data && !isEqual(data, prevPopupData)) {
      setPopupData(data);
      prevPopupData = data;
      map.mapInstance.view.popup.content = portalMount;
      const alertLocation = data.alert.location_history[data.index];
      map.mapInstance.view.popup.location = {
        y: alertLocation.geodetic.latitude,
        x: alertLocation.geodetic.longitude,
      };
      map.mapInstance.view.popup.visible = true;
      map.mapInstance.view.popup.alignment = 'top-center';
    }
  }

  /**
   * Function to clear all markers and circles from all layers
   */

  const clearAll = async () => {
    await new Promise(resolve => {
      clearAllMarkers();
      clearAllCircles();
      resolve();
    });
  };

  async function clearAllMarkers() {
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI:
          if (alertMarkersLayer) {
            alertMarkersLayer.removeAll();
          }
          if (aaAlertMarkersLayer) {
            aaAlertMarkersLayer.removeAll();
          }
          if (trainMarkersLayer) {
            trainMarkersLayer.removeAll();
          }
          if (testMarkersLayer) {
            testMarkersLayer.removeAll();
          }
          if (alertMarkers.length) {
            setAlertMarkers([]);
          }
          if (aaAlertMarkers.length) {
            setAAAlertMarkers([]);
          }
          if (trainMarkers.length) {
            setTrainMarkers([]);
          }
          if (testMarkers.length) {
            setTestMarkers([]);
          }
          break;
        default:
          return null;
      }
      resolve();
    });
  }

  async function clearAllCircles() {
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI:
          if (alertMarkerCirclesLayer) {
            alertMarkerCirclesLayer.removeAll();
          }
          if (alertMarkerCircles.length) {
            setAlertMarkerCircles([]);
          }
          if (aaAlertMarkerCirclesLayer) {
            aaAlertMarkerCirclesLayer.removeAll();
          }
          if (aaAlertMarkerCircles.length) {
            setaaAlertMarkerCircles([]);
          }
          if (trainMarkerCirclesLayer) {
            trainMarkerCirclesLayer.removeAll();
          }
          if (trainMarkerCircles.length) {
            setTrainMarkerCircles([]);
          }
          if (testMarkerCirclesLayer) {
            testMarkerCirclesLayer.removeAll();
          }
          if (testMarkerCircles.length) {
            setTestMarkerCircles([]);
          }
          break;
        default:
          return null;
      }
      resolve();
    });
  }

  useEffect(() => {
    return () => {
      prevPopupData = null;
    };
  }, []);

  // create and add marker and circle layers
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        if (
          map &&
          Object.keys(map?.mapInstance.map).length &&
          Object.keys(map?.mapInstance.view).length
        ) {
          if (!alertMarkerCirclesLayer) {
            createGraphicsLayer({
              configs: { id: ALERT_MARKER_CIRCLES_LAYER },
            }).then(layer => {
              map.mapInstance.map.layers.push(layer);
              setAlertMarkerCirclesLayer(layer);
            });
          }
          if (!alertMarkersLayer) {
            createGraphicsLayer({
              configs: { id: ALERT_MARKERS_LAYER },
            }).then(layer => {
              map.mapInstance.map.layers.push(layer);
              setAlertMarkersLayer(layer);
            });
          }
          if (!aaAlertMarkerCirclesLayer) {
            createGraphicsLayer({
              configs: { id: AA_ALERT_MARKER_CIRCLES_LAYER },
            }).then(layer => {
              map.mapInstance.map.layers.push(layer);
              setaaAlertMarkerCirclesLayer(layer);
            });
          }
          if (!aaAlertMarkersLayer) {
            createGraphicsLayer({
              configs: { id: AA_ALERT_MARKERS_LAYER },
            }).then(layer => {
              // Add graphics layer at the end of layers collection to ensure markers are displayed
              // on top of other layer types.
              // https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#add
              map.mapInstance.map.layers.push(layer);
              setaaAlertMarkersLayer(layer);
            });
          }
          if (!trainMarkerCirclesLayer) {
            createGraphicsLayer({
              configs: { id: TRAIN_ALERT_MARKER_CIRCLES_LAYER },
            }).then(layer => {
              map.mapInstance.map.layers.push(layer);
              setTrainMarkerCirclesLayer(layer);
            });
          }
          if (!trainMarkersLayer) {
            createGraphicsLayer({
              configs: { id: TRAIN_ALERT_MARKERS_LAYER },
            }).then(layer => {
              // Add graphics layer at the end of layers collection to ensure markers are displayed
              // on top of other layer types.
              // https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#add
              map.mapInstance.map.layers.push(layer);
              setTrainMarkersLayer(layer);
            });
          }
          if (!testMarkerCirclesLayer) {
            createGraphicsLayer({
              configs: { id: TEST_ALERT_MARKER_CIRCLES_LAYER },
            }).then(layer => {
              map.mapInstance.map.layers.push(layer);
              setTestMarkerCirclesLayer(layer);
            });
          }
          if (!testMarkersLayer) {
            createGraphicsLayer({
              configs: { id: TEST_ALERT_MARKERS_LAYER },
            }).then(layer => {
              // Add graphics layer at the end of layers collection to ensure markers are displayed
              // on top of other layer types.
              // https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#add
              map.mapInstance.map.layers.push(layer);
              setTestMarkersLayer(layer);
            });
          }
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Clear all markers, remove click/hover handlers when a call was selected
  useEffect(() => {
    if (map && selectedCallerID) {
      clearAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCallerID]);

  // Add handling for marker clicks/hover with Esri maps
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        if (
          (alertMarkersLayer?.graphics && alertMarkers.length) ||
          (aaAlertMarkersLayer?.graphics && aaAlertMarkers.length) ||
          (trainMarkersLayer?.graphics && trainMarkers.length) ||
          (testMarkersLayer?.graphics && testMarkers.length) ||
          (alertMarkerCirclesLayer?.graphics && alertMarkerCircles.length) ||
          (aaAlertMarkerCirclesLayer?.graphics &&
            aaAlertMarkerCircles.length) ||
          (trainMarkerCirclesLayer?.graphics && trainMarkerCircles.length) ||
          (testMarkerCirclesLayer?.graphics && testMarkerCircles.length)
        ) {
          handleMarkerClick();
          handleMarkerHover();
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    alertMarkers,
    aaAlertMarkers,
    trainMarkers,
    testMarkers,
    alertMarkerCircles,
    aaAlertMarkerCircles,
    trainMarkerCircles,
    testMarkerCircles,
    activeQueueTab,
    isMapIconDisabled,
  ]);

  // Handle changing marker icon when hoveredID changes
  const prevHoveredAlert = usePrevious(hoveredAlert);
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI: {
        const allAlertMarkers = [
          ...alertMarkers,
          ...aaAlertMarkers,
          ...trainMarkers,
          ...testMarkers,
        ];
        const allAlerts = {
          ...alerts,
          ...aaAlerts,
          ...trainAlerts,
          ...testAlerts,
        };
        if (isAllView) {
          if (prevHoveredAlert) {
            // handles hovering alerts from the queue without hovering the map
            const prevHoveredMarker = allAlertMarkers.find(
              marker => marker?.custom?.alertID === prevHoveredAlert?.alert_id,
            );
            if (prevHoveredMarker) {
              updateMarkerIcon(
                prevHoveredMarker,
                prevHoveredAlert,
                activeQueueTab,
                isAllView,
                false,
              );
            }
          }
          if (!hoveredAlert) {
            handlePopup();
          }
          if (allAlertMarkers.length) {
            const hoveredMarker = allAlertMarkers.find(
              marker => marker?.custom?.alertID === hoveredAlert?.alert_id,
            );
            if (hoveredMarker) {
              const data = hoveredAlert;
              handlePopup({ alert: data, index: 0 });
              // change found marker's icon on hover
              updateMarkerIcon(
                hoveredMarker,
                hoveredAlert,
                activeQueueTab,
                isAllView,
                false,
                'hover',
              );
            }
            if (prevHoveredAlert && hoveredAlert) {
              const prevHoveredMarker = allAlertMarkers.find(
                marker =>
                  marker?.custom?.alertID === prevHoveredAlert?.alert_id,
              );
              if (prevHoveredMarker) {
                const data = hoveredAlert;
                handlePopup({ alert: data, index: 0 });
                // recover found marker's icon from hover
                updateMarkerIcon(
                  prevHoveredMarker,
                  prevHoveredAlert,
                  activeQueueTab,
                  isAllView,
                  false,
                );
              }
            }
          }
        } else {
          if (hoveredAlert?.alert_id === selectedAlertID) {
            // This prevents handlePopup from triggering twice (here and handleMarkerHover) when hovering over the marker on the map
            // the rest of handling is done in the esriMarkerHoverHandler as here we can't know if a historic location
            // as it is a part of the hoveredMarker
            if (hoveredFrom === 'queue') {
              const data = allAlerts[selectedAlertID];
              handlePopup({ alert: data, index: 0 });
            }
          } else {
            handlePopup();
          }
        }
        break;
      }
      default:
        return null;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredAlert]);

  // Clear all markers and redraw
  useEffect(() => {
    if (
      alertMarkersLayer &&
      alertMarkerCirclesLayer &&
      aaAlertMarkersLayer &&
      aaAlertMarkerCirclesLayer &&
      trainMarkersLayer &&
      trainMarkerCirclesLayer &&
      testMarkersLayer &&
      testMarkerCirclesLayer &&
      (isAllView || selectedAlertID)
    ) {
      clearAll().then(() => {
        drawMarkers();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAllView,
    selectedAlertID,
    selectedCallerID,
    alertsBackfilledAt,
    alertMarkersLayer,
    aaAlertMarkersLayer,
    trainMarkersLayer,
    testMarkersLayer,
    aaAlertMarkerCirclesLayer,
    alertMarkerCirclesLayer,
    trainMarkerCirclesLayer,
    testMarkerCirclesLayer,
  ]);

  // Draw markers for Alerts
  async function drawMarkers() {
    const allAlerts = { ...alerts, ...aaAlerts, ...trainAlerts, ...testAlerts };
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI:
          if (isAllView) {
            if (Object.keys(allAlerts).length) {
              // Assume the GraphicsLayer is already created
              const markers = Object.values(allAlerts)
                .map(alert => {
                  const alertID = alert.alert_id;
                  const emergencyType = alert.emergency_type.name;

                  const shouldShowAlert = !isLocationRestricted(alert);

                  const layer =
                    emergencyType === ACTIVE_ASSAILANT
                      ? aaAlertMarkersLayer
                      : getIsTrainIncident(emergencyType, true)
                        ? trainMarkersLayer
                        : emergencyType.startsWith('TEST')
                          ? testMarkersLayer
                          : alertMarkersLayer;

                  if (!shouldShowAlert) {
                    return null;
                  }

                  const latlng = {
                    lat: alert.location.geodetic?.latitude,
                    lng: alert.location.geodetic?.longitude,
                  };
                  const isOpaque =
                    !isRegularAlert(alert) &&
                    activeQueueTab === EMERGENCY_CALLS &&
                    alert?.status.name === ACCEPTED;
                  const markerIcon = getAlertMarkerIcon(
                    isAllView,
                    false,
                    alert,
                    isOpaque,
                  );
                  return createMarker({
                    mapObject: map,
                    options: {
                      point: latlng,
                      icon: markerIcon,
                      iconSize: getAlertMarkerSize(isAllView),
                      custom: {
                        alertID,
                      },
                      layer: layer,
                    },
                    markerType: MARKER_TYPE_GRAPHIC,
                  }).then(resolvedMarker => {
                    layer.add(resolvedMarker.instance);
                    return resolvedMarker;
                  });
                })
                .filter(marker => marker !== null);
              Promise.all(markers).then(resolvedMarkers => {
                const {
                  aaaMarkers,
                  regularMarkers,
                  trainMarkers,
                  testMarkers,
                } = resolveMarkers(resolvedMarkers, allAlerts);
                setAAAlertMarkers(aaaMarkers);
                setAlertMarkers(regularMarkers);
                setTrainMarkers(trainMarkers);
                setTestMarkers(testMarkers);
              });
            }
          } else if (selectedAlertID) {
            const { location_history } = allAlerts[selectedAlertID] || [];
            const emergencyType =
              allAlerts[selectedAlertID].emergency_type.name;

            if (location_history.length) {
              panMapTo({
                mapProvider: ESRI,
                mapInstance: map.mapInstance,
                latitude: parseFloat(location_history[0].geodetic.latitude),
                longitude: parseFloat(location_history[0].geodetic.longitude),
                zoom: 18,
              });
            }
            const markers = location_history.map((location, index) => {
              const latlng = {
                lat: location.geodetic.latitude,
                lng: location.geodetic.longitude,
              };

              const markerIcon = getAlertMarkerIcon(
                isAllView,
                index === 0,
                allAlerts[selectedAlertID],
              );
              const layer =
                emergencyType === ACTIVE_ASSAILANT
                  ? aaAlertMarkersLayer
                  : getIsTrainIncident(emergencyType, true)
                    ? trainMarkersLayer
                    : emergencyType.startsWith('TEST')
                      ? testMarkersLayer
                      : alertMarkersLayer;

              return createMarker({
                mapObject: map,
                options: {
                  point: latlng,
                  icon: markerIcon,
                  iconSize: getAlertMarkerSize(isAllView, index === 0),
                  custom: {
                    alertID: selectedAlertID,
                  },
                  layer: layer,
                },
                markerType: MARKER_TYPE_GRAPHIC,
              }).then(resolvedMarker => {
                layer.add(resolvedMarker.instance);
                return resolvedMarker;
              });
            });

            Promise.all(markers).then(resolvedMarkers => {
              const { aaaMarkers, regularMarkers, trainMarkers, testMarkers } =
                resolveMarkers(resolvedMarkers, allAlerts);
              setAAAlertMarkers(aaaMarkers);
              setAlertMarkers(regularMarkers);
              setTrainMarkers(trainMarkers);
              setTestMarkers(testMarkers);
            });

            const markerCircles = location_history.map((location, index) => {
              const latlng = {
                lat: location.geodetic.latitude,
                lng: location.geodetic.longitude,
              };

              const circleOptions = {
                center: [latlng.lng, latlng.lat],
                radius: location.geodetic.uncertainty_radius,
              };

              const circleColor = getAlertCircleColor(index > 0);
              const layer =
                emergencyType === ACTIVE_ASSAILANT
                  ? aaAlertMarkerCirclesLayer
                  : getIsTrainIncident(emergencyType, true)
                    ? trainMarkerCirclesLayer
                    : emergencyType.startsWith('TEST')
                      ? testMarkerCirclesLayer
                      : alertMarkerCirclesLayer;
              return createCircle({
                mapObject: map,
                circleOptions: {
                  ...circleOptions,
                  custom: {
                    alertID: selectedAlertID,
                  },
                  layer: layer,
                },
                graphicOptions: {
                  symbol: {
                    ...baseEsriCircle,
                    color: circleColor,
                    outline: {
                      ...baseEsriCircle.outline,
                      color: circleColor,
                    },
                  },
                  visible: true,
                },
              }).then(resolvedMarkerCircle => {
                layer.add(resolvedMarkerCircle.instance);
                return resolvedMarkerCircle;
              });
            });

            Promise.all(markerCircles).then(resolvedMarkerCircle => {
              emergencyType === ACTIVE_ASSAILANT
                ? setaaAlertMarkerCircles(resolvedMarkerCircle)
                : getIsTrainIncident(emergencyType, true)
                  ? setTrainMarkerCircles(resolvedMarkerCircle)
                  : emergencyType.startsWith('TEST')
                    ? setTestMarkerCircles(resolvedMarkerCircle)
                    : setAlertMarkerCircles(resolvedMarkerCircle);
            });
          }
          break;
        default:
          return null;
      }
      resolve();
    });
  }

  // When a marker is hovered, set the alertID to a state hook which callback the onMarkerHover
  // function from JurisdictionView.js.
  useEffect(() => {
    if (alertMarkerHoveredID) {
      handleAlertMarkerHover(alertMarkerHoveredID);
    } else {
      handleAlertMarkerHover(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertMarkerHoveredID]);

  return (
    <>
      <AlertMarkers
        activeQueueTab={activeQueueTab}
        map={map}
        alerts={alerts}
        alertMarkersLayer={alertMarkersLayer}
        alertMarkers={alertMarkers}
        setAlertMarkers={setAlertMarkers}
        alertMarkerCirclesLayer={alertMarkerCirclesLayer}
        alertMarkerCircles={alertMarkerCircles}
        setAlertMarkerCircles={setAlertMarkerCircles}
        onMarkerAdd={handleAlertMarkerAdd}
        onRemoveID={handleAlertMarkerRemove}
        removedID={removedID}
        popupData={popupData}
        portalMount={portalMount}
      />
      <AAAMarkers
        activeQueueTab={activeQueueTab}
        map={map}
        onMarkerAdd={handleAlertMarkerAdd}
        onRemoveID={handleAlertMarkerRemove}
        handleMarkerHover={handleMarkerHover}
        removedID={removedID}
        alerts={aaAlerts}
        aaAlertMarkers={aaAlertMarkers}
        setAAAlertMarkers={setAAAlertMarkers}
        aaAlertMarkersLayer={aaAlertMarkersLayer}
        aaAlertMarkerCircles={aaAlertMarkerCircles}
        setaaAlertMarkerCircles={setaaAlertMarkerCircles}
        aaAlertMarkerCirclesLayer={aaAlertMarkerCirclesLayer}
        popupData={popupData}
        portalMount={portalMount}
      />
      <TrainMarkers
        activeQueueTab={activeQueueTab}
        map={map}
        onMarkerAdd={handleAlertMarkerAdd}
        onRemoveID={handleAlertMarkerRemove}
        handleMarkerHover={handleMarkerHover}
        removedID={removedID}
        alerts={trainAlerts}
        trainMarkers={trainMarkers}
        setTrainMarkers={setTrainMarkers}
        trainMarkersLayer={trainMarkersLayer}
        trainMarkerCircles={trainMarkerCircles}
        setTrainMarkerCircles={setTrainMarkerCircles}
        trainMarkerCirclesLayer={trainMarkerCirclesLayer}
        popupData={popupData}
        portalMount={portalMount}
      />
      <TestMarkers
        activeQueueTab={activeQueueTab}
        map={map}
        onMarkerAdd={handleAlertMarkerAdd}
        onRemoveID={handleAlertMarkerRemove}
        removedID={removedID}
        alerts={testAlerts}
        testMarkers={testMarkers}
        setTestMarkers={setTestMarkers}
        testMarkersLayer={testMarkersLayer}
        testMarkerCircles={testMarkerCircles}
        setTestMarkerCircles={setTestMarkerCircles}
        testMarkerCirclesLayer={testMarkerCirclesLayer}
        popupData={popupData}
        portalMount={portalMount}
      />
    </>
  );
};

export default AllAlertMarkers;
